/**
 * useBlogPost Hook
 * 
 * Custom React hook for fetching a single blog post by ID or slug.
 * Provides loading states, error handling, and data management.
 */

import { useState, useEffect, useCallback } from 'react';
import { blogService } from '../services/blogService';
import type { BlogPost } from '../types/blog.types';
import { ApiError } from '../services/apiClient';

/**
 * Hook return type
 */
export interface UseBlogPostReturn {
  /** Blog post data */
  blog: BlogPost | null;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: string | null;
  /** Function to refetch blog post */
  refetch: () => Promise<void>;
}

/**
 * Custom hook for fetching a single blog post by ID
 */
export const useBlogPostById = (id: string | null): UseBlogPostReturn => {
  const [blog, setBlog] = useState<BlogPost | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBlog = useCallback(async () => {
    if (!id) {
      setBlog(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await blogService.getBlogById(id);
      if (data) {
        setBlog(data);
      } else {
        setError('Blog post not found');
        setBlog(null);
      }
    } catch (err) {
      const errorMessage =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
          ? err.message
          : 'Failed to fetch blog post';
      setError(errorMessage);
      setBlog(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBlog();
  }, [fetchBlog]);

  return {
    blog,
    isLoading,
    error,
    refetch: fetchBlog,
  };
};

/**
 * Custom hook for fetching a single blog post by slug
 */
export const useBlogPostBySlug = (slug: string | null): UseBlogPostReturn => {
  const [blog, setBlog] = useState<BlogPost | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBlog = useCallback(async () => {
    if (!slug) {
      setBlog(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await blogService.getBlogBySlug(slug);
      if (data) {
        setBlog(data);
      } else {
        setError('Blog post not found');
        setBlog(null);
      }
    } catch (err) {
      const errorMessage =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
          ? err.message
          : 'Failed to fetch blog post';
      setError(errorMessage);
      setBlog(null);
    } finally {
      setIsLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchBlog();
  }, [fetchBlog]);

  return {
    blog,
    isLoading,
    error,
    refetch: fetchBlog,
  };
};

